import type { NotificationActionData, PostAuthOptions } from 'types';

const DAY_IN_MS = 1000 * 60 * 60 * 24;
export const NOTIFICATION_INTENT_KEY = 'notificationIntent';
const NOTIFICATION_DATA_KEY = 'notificationData';

export const NOTIFICATION_INTENTS_CHANGED_EVENT =
  'notification-intents-changed';

function emitNotificationIntentsChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(NOTIFICATION_INTENTS_CHANGED_EVENT));
  }
}

type NotificationIntent = {
  expires: number;
  productId: number;
};

export function getBrowser(userAgent: string | null) {
  const match = userAgent?.match(/firefox|chrome|safari|instagram|msie/i);

  if (!match) return 'unknown';

  const [browser] = match;

  return browser!.toLowerCase();
}

export function isAppleMobileDevice(userAgent: string | null) {
  if (!userAgent) return false;

  return /iPhone|iPad|iPod/i.test(userAgent);
}

export function isInAppBrowser(userAgent: string = '') {
  return /instagram|fban|fbav|twitter/i.test(userAgent);
}

export function getNotificationData(): NotificationActionData[] {
  try {
    const raw = localStorage.getItem(NOTIFICATION_DATA_KEY);

    if (!raw) return [];

    const parsed = JSON.parse(raw);
    const list: NotificationActionData[] = Array.isArray(parsed)
      ? parsed
      : [parsed];

    return list.filter((item) => item && Date.now() < item.expires);
  } catch {
    return [];
  }
}

export function clearNotificationData() {
  localStorage.removeItem(NOTIFICATION_DATA_KEY);
}

export function storeNotificationData(postAuthOptions: PostAuthOptions) {
  const data = getNotificationData().filter(
    (item) => item.productId !== postAuthOptions.productId
  );

  data.push({
    expires: Date.now() + DAY_IN_MS,
    legacyProductId: postAuthOptions.legacyProductId ?? '',
    productId: postAuthOptions.productId ?? '',
    productName: postAuthOptions.productName ?? '',
    sizes: postAuthOptions.sizes ?? []
  });

  localStorage.setItem(NOTIFICATION_DATA_KEY, JSON.stringify(data));

  addNotificationIntent(postAuthOptions.productId);
}

function getNotificationIntents(): NotificationIntent[] {
  try {
    const raw = localStorage.getItem(NOTIFICATION_INTENT_KEY);

    if (!raw) return [];

    const intents = JSON.parse(raw) as NotificationIntent[];

    return intents.filter((intent) => Date.now() < intent.expires);
  } catch {
    return [];
  }
}

export function addNotificationIntent(productId?: string) {
  const id = Number(productId);

  if (!id) return;

  const intents = getNotificationIntents().filter(
    (intent) => intent.productId !== id
  );

  intents.push({ expires: Date.now() + DAY_IN_MS, productId: id });

  localStorage.setItem(NOTIFICATION_INTENT_KEY, JSON.stringify(intents));
  emitNotificationIntentsChanged();
}

export function getPendingNotificationProductIds(): number[] {
  return getNotificationIntents().map((intent) => intent.productId);
}

export function clearNotificationIntents() {
  localStorage.removeItem(NOTIFICATION_INTENT_KEY);
  emitNotificationIntentsChanged();
}
